import { zodResolver } from '@hookform/resolvers/zod'
import { useMeasure } from '@uidotdev/usehooks'
import { Modal } from 'antd'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import AtomSteps from '@/components/Atoms/AtomSteps/AtomSteps'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'

import {
  type CreatedNomenclature,
  singularImportApi,
} from './api/singularImportApi'
import { useNomenclatureDictionaries } from './hooks/useNomenclatureDictionaries'
import { toNomenclatureCreationRequest } from './lib/singularImportRequest'
import { buildCreatedNomenclatureItems } from './lib/singularImportResult'
import { getTreeNodePathLabels } from './lib/singularImportTreePath'
import NomenclatureCreatedCard from './NomenclatureCreatedCard/NomenclatureCreatedCard'
import NomenclatureCreationForm from './NomenclatureCreationForm/NomenclatureCreationForm'
import NomenclatureHierarchyFields from './NomenclatureHierarchyFields/NomenclatureHierarchyFields'
import {
  nomenclatureCreationSchema,
  type NomenclatureCreationType,
} from './SingularImport.config'
import cn from './SingularImport.module.scss'

type SingularImportStep = 'form' | 'result'

const STEP_OPTIONS = [
  { value: 'form' as const, label: 'Заполнение карточки' },
  { value: 'result' as const, label: 'Результат' },
]

const DEFAULT_VALUES: Partial<NomenclatureCreationType> = {
  supplierArticles: [],
}

type CreatedResult = {
  nomenclature: CreatedNomenclature
  values: NomenclatureCreationType
}

type SingularImportProps = {
  isOrganisationSelected: boolean
}

function SingularImport({ isOrganisationSelected }: SingularImportProps) {
  const [step, setStep] = useState<SingularImportStep>('form')
  const [created, setCreated] = useState<CreatedResult | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const isCreatingRef = useRef(false)
  const [modal, contextHolder] = Modal.useModal()
  const dictionaries = useNomenclatureDictionaries()
  const [formColumnRef, { height: formColumnHeight }] = useMeasure()

  const form = useForm<NomenclatureCreationType>({
    resolver: zodResolver(nomenclatureCreationSchema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onTouched',
    shouldFocusError: false,
  })

  const createNomenclature = async (values: NomenclatureCreationType) => {
    if (isCreatingRef.current) return
    isCreatingRef.current = true
    setIsCreating(true)
    try {
      const nomenclature = await singularImportApi.createNomenclature(
        toNomenclatureCreationRequest(values)
      )
      setCreated({ nomenclature, values })
      setStep('result')
      toast.success('Номенклатура создана', {
        description: `${nomenclature.article} — ${nomenclature.description}`,
      })
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      isCreatingRef.current = false
      setIsCreating(false)
    }
  }

  const handleCreate = form.handleSubmit(createNomenclature, () =>
    handleFormSubmitError(false)
  )

  const changeStep = (nextStep: SingularImportStep) => {
    if (nextStep === 'form' || created) setStep(nextStep)
    else void handleCreate()
  }

  const startNewNomenclature = () => {
    form.reset(DEFAULT_VALUES)
    setFormKey((key) => key + 1)
    setCreated(null)
    setStep('form')
  }

  return (
    <div className={cn.card}>
      {contextHolder}
      <div className={cn.steps}>
        <AtomSteps
          options={STEP_OPTIONS}
          value={step}
          onChange={changeStep}
          ariaLabel="Шаг создания номенклатуры"
          isOrganisationSelected={isOrganisationSelected}
        />
      </div>
      <form
        className={cn.content}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void handleCreate()
        }}
      >
        <div key={formKey} className={cn.formStep} hidden={step !== 'form'}>
          <div ref={formColumnRef} className={cn.formColumn}>
            <NomenclatureCreationForm
              form={form}
              dictionaries={dictionaries}
              isOrganisationSelected={isOrganisationSelected}
            />
            <AtomButton htmlType="submit" loading={isCreating} fullWidth>
              Создать
            </AtomButton>
          </div>
          <NomenclatureHierarchyFields
            form={form}
            dictionaries={dictionaries}
            maxHeight={formColumnHeight}
            isOrganisationSelected={isOrganisationSelected}
          />
        </div>
        {step === 'result' && created && (
          <NomenclatureCreatedCard
            description={created.nomenclature.description}
            items={buildCreatedNomenclatureItems(
              created.nomenclature,
              created.values,
              {
                group: getTreeNodePathLabels(
                  dictionaries.nomenclatureGroupsTree,
                  created.values.nomenclatureGroup
                ),
                category: getTreeNodePathLabels(
                  dictionaries.categoriesTree,
                  created.values.category
                ),
              }
            )}
            isOrganisationSelected={isOrganisationSelected}
          />
        )}
        {step === 'result' && (
          <div className={cn.footer}>
            <AtomButton onClick={startNewNomenclature}>
              Создать номенклатуру повторно
            </AtomButton>
          </div>
        )}
      </form>
    </div>
  )
}

export default SingularImport
